import { useEffect, useState } from 'react';

import { refetchLoyaltyUserSafely } from 'features/loyalty-user/refetch-loyalty-user-safely';

import { useLoyaltyUser } from 'state/loyalty/hooks/use-loyalty-user';
import {
  ShortCodePaymentResultPollingStatus,
  useShortCodePaymentResultPollStatus,
} from 'state/loyalty/in-restaurant-redemption/hooks/use-short-code-payment-result-poll-status';
import { useInRestaurantRedemptionContext } from 'state/loyalty/in-restaurant-redemption/in-restaurant-redemption-context';
import { ShortCodeState } from 'state/loyalty/in-restaurant-redemption/types';
import { useLoyaltyContext } from 'state/loyalty/loyalty-context';

import { usePollingInterval } from '../hooks/use-polling-interval';
import ShortCodePaymentResultModal from '../modals/short-code-payment-result-modal';

export function ShortCodePaymentResultPoller() {
  const { refetchLoyaltyUser } = useLoyaltyContext();

  const [currentDateTime, setCurrentDateTime] = useState<Date>(new Date(0));
  const [showPaymentResultModal, setShowPaymentResultModal] = useState(false);
  const [shouldPollPaymentResult, setShouldPollPaymentResult] = useState(false);

  const {
    resetInRestaurantRedemption,
    shouldResetPollingResultStatus,
    setShouldResetPollingResultStatus,
    shortCodeState,
    isQuickPayEnabled,
  } = useInRestaurantRedemptionContext();

  const { loyaltyUser } = useLoyaltyUser();
  const loyaltyId = loyaltyUser?.id;

  const pollInterval = usePollingInterval();

  const {
    pollingShortCodePaymentResultStatus,
    resetPollingPaymentResultStatus,
    paymentResult,
  } = useShortCodePaymentResultPollStatus({
    loyaltyId,
    skipPolling: !shouldPollPaymentResult,
    pollInterval,
    currentDateTime,
  });

  useEffect(() => {
    setCurrentDateTime(new Date());
  }, []);

  useEffect(() => {
    if (pollingShortCodePaymentResultStatus === ShortCodePaymentResultPollingStatus.Completed) {
      if (!showPaymentResultModal) {
        setShowPaymentResultModal(true);
      }
    }
  }, [pollingShortCodePaymentResultStatus, showPaymentResultModal]);

  useEffect(() => {
    if (shouldResetPollingResultStatus) {
      resetPollingPaymentResultStatus();
      setCurrentDateTime(new Date());
      setShouldResetPollingResultStatus(false);
    }
  }, [
    resetPollingPaymentResultStatus,
    setShouldResetPollingResultStatus,
    shouldResetPollingResultStatus,
  ]);

  useEffect(() => {
    if (isQuickPayEnabled && shortCodeState === ShortCodeState.Claimed) {
      setShouldPollPaymentResult(
        pollingShortCodePaymentResultStatus !== ShortCodePaymentResultPollingStatus.Completed
      );
    }
  }, [
    isQuickPayEnabled,
    pollingShortCodePaymentResultStatus,
    setShouldPollPaymentResult,
    shortCodeState,
    shouldPollPaymentResult,
  ]);

  const onConfirmationModalAction = () => {
    resetInRestaurantRedemption();

    // re-fetching user points
    refetchLoyaltyUserSafely(refetchLoyaltyUser, 'short-code payment confirmation');

    // Note that we need sometime for pollingShortCodePaymentResultStatus
    // to reset after resetInRestaurantRedemption is called
    setTimeout(() => {
      setShowPaymentResultModal(false);
    }, 1);
  };

  return (
    <>
      {showPaymentResultModal && (
        <ShortCodePaymentResultModal
          onAction={onConfirmationModalAction}
          onDismiss={onConfirmationModalAction}
          paymentResult={paymentResult}
        />
      )}
    </>
  );
}
